import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Plus } from 'lucide-react';
import apiService from '../services/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { slideUp, fadeIn } from '@/lib/animations';

// Interface for content type
interface ContentType {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

interface VideoUploaderProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete?: (uploadedVideo: any) => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = (props) => {
  const { open, onClose, onUploadComplete } = props;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedContentType, setSelectedContentType] = useState<string>('');
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingContentTypes, setLoadingContentTypes] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fetchingRef = useRef(false);
  const contentTypesCacheRef = useRef<ContentType[] | null>(null);
  
  // Fetch content types when component mounts
  useEffect(() => {
    let mounted = true;
    
    const getContentTypes = async () => {
      if (!open || fetchingRef.current) return;
      
      // Check cache first
      if (contentTypesCacheRef.current) {
        setContentTypes(contentTypesCacheRef.current);
        return;
      }
      
      try {
        fetchingRef.current = true;
        setLoadingContentTypes(true);
        const response = await apiService.getContentTypes();
        
        if (!mounted) return;
        
        if (response.data?.data?.contentTypes) {
          const types = response.data.data.contentTypes;
          console.log('Content types received:', types);
          setContentTypes(types);
          // Cache the content types
          contentTypesCacheRef.current = types;
        }
      } catch (error: any) {
        if (!mounted) return;
        console.error('Error fetching content types:', error);
        setErrorMessage('Failed to load content types: ' + (error.message || 'Unknown error'));
      } finally {
        if (mounted) {
          setLoadingContentTypes(false);
        }
        fetchingRef.current = false;
      }
    };

    getContentTypes();
    
    return () => {
      mounted = false;
      fetchingRef.current = false;
    };
  }, [open]);
  
  // Handle drag events
  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type.startsWith('video/')) {
      setSelectedFile(files[0]);
    }
  };
  
  // Handle content type selection
  const handleContentTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    console.log('Content type selected:', value);
    setSelectedContentType(value);
  };
  
  // Handle file selection
  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };
  
  // Reset the uploader
  const resetUploader = () => {
    setSelectedFile(null);
    setSelectedContentType('');
    setUploading(false);
    setProgress(0);
    setUploadSuccess(false);
    setUploadError(false);
    setErrorMessage('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Handle dialog close
  const handleClose = () => {
    resetUploader();
    onClose();
  };
  
  // Validate before upload
  const validateUpload = (): boolean => {
    if (!selectedFile) {
      setErrorMessage('Please select a video file');
      return false;
    }
    
    if (!selectedContentType) {
      setErrorMessage('Please select a content type');
      return false;
    }
    
    return true;
  };
  
  // Handle upload
  const handleUpload = async () => {
    if (!validateUpload()) {
      setUploadError(true);
      return;
    }
    
    // Add auth token check
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUploadError(true);
      setErrorMessage('Authentication required. Please log in again.');
      return;
    }
    
    try {
      setUploading(true);
      setProgress(0);
      setUploadError(false);
      
      const selectedType = contentTypes.find(type => type.id === selectedContentType);
      console.log('Uploading with content type:', selectedType);
      
      const response = await apiService.uploadFile(
        '/videos/upload',
        selectedFile as File,
        selectedContentType,
        (progress) => {
          setProgress(progress);
        }
      );
      
      // Improved logging for response
      console.log('Upload complete with response:', response);
      console.log('Uploaded video data structure:', response.data?.data);
      
      if (response.data?.data?.original_url) {
        console.log('Original URL in response:', response.data.data.original_url);
      }
      
      setUploading(false);
      setUploadSuccess(true);
      
      if (onUploadComplete && response.data && response.data.data) {
        // Ensure the response data has the expected structure
        const videoData = response.data.data;
        console.log('Sending video data to parent:', videoData);
        onUploadComplete(videoData);
      } else {
        console.warn('No data to pass to parent component');
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploading(false);
      setUploadError(true);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        setErrorMessage('Authentication required. Please log in again.');
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        setErrorMessage(error.message || 'Failed to upload video');
      }
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
      <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Upload Video</h2>
            <p className="text-sm text-muted-foreground">
              Upload your video and select its content type
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Upload Area */}
        <div
          className={cn(
            "relative mt-2 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 text-center",
            isDragging ? "border-primary" : "border-border",
            uploadSuccess ? "border-green-500" : "",
            uploadError ? "border-red-500" : ""
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {selectedFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center">
                <video
                  className="max-h-32 rounded"
                  src={URL.createObjectURL(selectedFile)}
                />
              </div>
              <p className="text-sm font-medium">{selectedFile.name}</p>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600"
                onClick={() => setSelectedFile(null)}
              >
                Remove
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Upload className="h-6 w-6 text-primary" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Drag and drop your video here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  Supported formats: MP4, MOV, AVI
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={handleFileSelect}
                className="mt-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                Select Video
              </Button>
            </div>
          )}
        </div>

        {/* Content Type Selection */}
        {selectedFile && !uploading && !uploadSuccess && !uploadError && (
          <div className="mt-4">
            <label className="text-sm font-medium">Content Type</label>
            <select
              value={selectedContentType}
              onChange={(e) => setSelectedContentType(e.target.value)}
              className={cn(
                "mt-2 block w-full rounded-md border bg-background px-3 py-2",
                uploadError && !selectedContentType
                  ? "border-red-500"
                  : "border-input"
              )}
              disabled={loadingContentTypes}
            >
              <option value="" disabled>
                {loadingContentTypes ? "Loading..." : "Select a content type"}
              </option>
              {contentTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            {uploadError && !selectedContentType && (
              <p className="mt-1 text-xs text-red-500">
                Please select a content type
              </p>
            )}
          </div>
        )}

        {/* Progress and Status */}
        {uploading && (
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span>Uploading...</span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {uploadSuccess && (
          <div className="mt-4 text-center text-green-500">
            <p className="font-medium">Upload successful!</p>
          </div>
        )}

        {uploadError && (
          <div className="mt-4 text-center text-red-500">
            <p className="font-medium">{errorMessage}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          {selectedFile && !uploadSuccess && (
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedContentType}
              className="relative"
            >
              {uploading ? (
                <div className="flex items-center">
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Uploading...
                </div>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoUploader; 